/**
 * Context doc schema. PRD-003 DATA-02.
 *
 * Five fixed sections, each with optional/additive sub-fields. Sub-field
 * additions land in this file as additions, with the contextDocSections
 * schema version bumped in JSONB_SCHEMA_VERSIONS (common.ts).
 *
 * The `sections` payload is stored as JSONB in Postgres with a sibling
 * `sections_schema_version` column (ARCH-001 §3). The version branded onto
 * the wire payload via `versionedSchema` must match the column value.
 */

import { z } from 'zod';
import {
  JSONB_SCHEMA_VERSIONS,
  isoTimestampSchema,
  uuidSchema,
  versionedSchema,
} from './common.js';

/**
 * Section 1: Company. Stage and where-they-sell are free-form to allow the
 * sub-field shape to evolve as the engagement deepens.
 */
export const companySectionSchema = z.object({
  name: z.string().optional(),
  stage: z.string().optional(),
  what_they_sell: z.string().optional(),
  where_they_sell: z.string().optional(),
  // Sub-fields can grow over time via additive JSONB. Bump the schema version
  // when locking a new sub-field name. Free-form extras are recorded under
  // `additional` to keep them out of the typed accessor surface.
  additional: z.record(z.string(), z.unknown()).optional(),
});
export type CompanySection = z.infer<typeof companySectionSchema>;

/**
 * Section 2: Audiences.
 */
export const audiencesSectionSchema = z.object({
  primary_buyer: z.string().optional(),
  secondary_buyers: z.array(z.string()).optional(),
  users: z.string().optional(),
  who_not_serving: z.string().optional(),
  additional: z.record(z.string(), z.unknown()).optional(),
});
export type AudiencesSection = z.infer<typeof audiencesSectionSchema>;

/**
 * Section 3: Strategic context.
 */
export const strategicContextSectionSchema = z.object({
  current_one_liner: z.string().optional(),
  current_positioning: z.string().optional(),
  competitors_of_note: z.array(z.string()).optional(),
  whats_working: z.string().optional(),
  biggest_blocker: z.string().optional(),
  additional: z.record(z.string(), z.unknown()).optional(),
});
export type StrategicContextSection = z.infer<typeof strategicContextSectionSchema>;

/**
 * Section 4: Voice and constraints.
 */
export const voiceConstraintsSectionSchema = z.object({
  voice_attributes: z.array(z.string()).optional(),
  banned_phrases: z.array(z.string()).optional(),
  tone: z.string().optional(),
  language: z.string().optional(),
  regulatory_constraints: z.string().optional(),
  additional: z.record(z.string(), z.unknown()).optional(),
});
export type VoiceConstraintsSection = z.infer<typeof voiceConstraintsSectionSchema>;

/**
 * Section 5: Initial priorities.
 */
export const initialPrioritiesSectionSchema = z.object({
  first_30_days: z.string().optional(),
  what_to_deliver_first: z.string().optional(),
  what_to_avoid_first_month: z.string().optional(),
  additional: z.record(z.string(), z.unknown()).optional(),
});
export type InitialPrioritiesSection = z.infer<typeof initialPrioritiesSectionSchema>;

/**
 * Sub-field provenance. PRD-003 PR-24 + DATA-02 ("Pulled" badge).
 *
 * Each populated sub-field path can optionally carry an entry indicating
 * which `your_notes` row it was promoted from. The map key is a JSON-pointer
 * style path into `sections` (e.g., "/strategic_context/biggest_blocker").
 */
export const subFieldProvenanceSchema = z.record(
  z.string(),
  z.object({
    promoted_from_note_id: uuidSchema,
    promoted_at: isoTimestampSchema,
  }),
);
export type SubFieldProvenance = z.infer<typeof subFieldProvenanceSchema>;

/**
 * Five-section payload. JSONB column body.
 */
export const contextDocSectionsPayloadSchema = z.object({
  company: companySectionSchema.optional(),
  audiences: audiencesSectionSchema.optional(),
  strategic_context: strategicContextSectionSchema.optional(),
  voice_and_constraints: voiceConstraintsSectionSchema.optional(),
  initial_priorities: initialPrioritiesSectionSchema.optional(),
  provenance: subFieldProvenanceSchema.optional(),
});
export type ContextDocSectionsPayload = z.infer<typeof contextDocSectionsPayloadSchema>;

/**
 * Versioned wrapper that any consumer reading from `context_docs.sections`
 * should branch on. Matches DB column `sections_schema_version`.
 */
export const contextDocSectionsSchema = versionedSchema(
  contextDocSectionsPayloadSchema,
  JSONB_SCHEMA_VERSIONS.contextDocSections,
);
export type ContextDocSections = z.infer<typeof contextDocSectionsSchema>;

/**
 * Context doc row. PRD-003 DATA-02 + DATA-12 (sections payload encrypted at rest).
 */
export const contextDocSchema = z.object({
  id: uuidSchema,
  subscriber_id: uuidSchema,
  sections: contextDocSectionsPayloadSchema,
  sections_schema_version: z
    .number()
    .int()
    .min(1)
    .default(JSONB_SCHEMA_VERSIONS.contextDocSections),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
  deleted_at: isoTimestampSchema.nullable(),
});
export type ContextDoc = z.infer<typeof contextDocSchema>;
